import {Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {
  UserSubscription,
  UserSubscriptionCategory,
  UserSubscriptionType
} from "../../../../../../user/user-subscription";
import {OneMonthFreeStep} from "../../one-month-free.component";
import {DbActiveModal} from "../../../../../../modules/modal/modal-ref";
import {FormControl} from "@angular/forms";
import {User} from "../../../../../../user/user";
import {Subscription} from "rxjs";
import {filter, first} from "rxjs/operators";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../../store";
import {ChosenPlan} from "../../../../payment-info/models/plan.model";
import {GoogleAnalyticsService} from "../../../../../services/google-analytics/google-analytics.service";
import {CookieService} from "ngx-cookie";
import planFeatures from "./one-week-free-demo__option/plans-features.json";
import {DbResponse} from "../../../../../interceptors/db-response";
import {AuthService} from "../../../../../../auth/auth.service";


@Component({
    selector: 'db-one-week-free-demo',
    templateUrl: './one-week-free-demo.component.html',
    styleUrls: ['./one-week-free-demo.component.scss'],
    standalone: false
})
export class OneWeekFreeDemoComponent implements OnInit {

  @Input() currentSubscription: UserSubscription;
  @Input() user: User;
  @Input() step: number = 2;
  @Input() showAnnual?: boolean = false;
  @Input() paidFeature?: boolean = false;
  @Input() couponCode?: string;
  @Input() domainName?: string;
  @Input() showBackButton?: boolean;
  @Input() isEcommerceWebsite?: boolean;
  @Input() hideTab?: boolean;
  @Input() englishCountries?: boolean;
  @Input() customDomainFlow?: boolean;

  @Output() onPlanChange = new EventEmitter<ChosenPlan>();
  @Output() onStepChange = new EventEmitter<OneMonthFreeStep>();

  public periodSwitch: FormControl;
  public loading: boolean;
  public isMobile: boolean;
  public monthlyPlans: boolean = true;
  public UserSubscriptionCategory = UserSubscriptionCategory;
  public userHostingSubscriptions: UserSubscription[];
  public subscriptionsTabs: object;
  public activeTab = 'business';
  public UserSubscriptionType = UserSubscriptionType;
  public planFeatures: any;
  public isTier1Country?: boolean;
  public applyCouponForPlatform?: boolean;
  public couponPercent?: number = 0;
  public hasFreeDays: boolean = false;

  private _subscriptions: Subscription[];

  constructor(
    private readonly activeModal: DbActiveModal,
    private readonly _googleAnalyticsService: GoogleAnalyticsService,
    private _store: Store<fromStore.DbStoreState>,
    private _cookieService: CookieService,
    private _authService: AuthService
  ) {
  }

  ngOnInit() {
    if (this.hideTab || (this.currentSubscription.trialClient && this.isEcommerceWebsite)) {
      this.activeTab = 'ecommerce';
    }
    this.periodSwitch = new FormControl(12);
    this.activeModal.resize(610, !this.hideTab || (this.isEcommerceWebsite && this.currentSubscription.trialClient) ? 1180 : 940);
    this.activeModal.whiteCloseBtn(true);
    this.isMobile = window.innerWidth <= 991;
    this.planFeatures = planFeatures;
    const getExperimentalCookie = this._cookieService.get('db-experiment');

    (this.hideTab || (this.currentSubscription.trialClient && this.isEcommerceWebsite)) && (this.activeTab = 'ecommerce');

    this.isTier1Country = getExperimentalCookie ? JSON.parse(getExperimentalCookie) && JSON.parse(getExperimentalCookie).t1PExp && JSON.parse(getExperimentalCookie).t1PExp === 'b' : false;
    this.getAvailableSubscriptions();
    this.addEventHandlers();
  }

  public openTab(tabId: string) {
    this.activeTab = tabId;
    sessionStorage.setItem("activeTab", this.activeTab);
  }


  public chooseSubscr(subscription: UserSubscription, coupon?) {
    const chosenPlan: ChosenPlan = {
      id: subscription.id,
      title: subscription.plan.title,
      price: subscription.plan.planPrice,
      planPrice: subscription.plan.price,
      type: this.periodSwitch.value === 12 ? 'year' : 'month',
      coupon: subscription.couponCode,
      periodNumber: subscription.plan.periodNumber,
    };
    this.onPlanChange.emit(chosenPlan);
    this.activeModal.whiteCloseBtn(false);
    this._sendEvent(chosenPlan);
  }

  public resizeToNormal(): void {
    this.activeModal.resize(600, 940);
  }

  private async getAvailableSubscriptions(): Promise<void> {
    const tabs = {
      "business": [],
      "ecommerce": [],
      "agency": []
    };

    const categories = [
      UserSubscriptionCategory.PERSONAL,
      UserSubscriptionCategory.PREMIUM,
      UserSubscriptionCategory.ENTERPRISE
    ];

    try {
      await new Promise<void>((resolve) => {
        this._store.select(fromStore.getUserLoaded)
          .pipe(first())
          .subscribe(() => resolve());
      });

      const availableSubscriptions = await this._store.select(fromStore.getUserAvailableHostingSubscriptions('platform'))
        .pipe(first(res => !!res.length))
        .toPromise();

      this.userHostingSubscriptions = availableSubscriptions.filter((subscription: any) =>
        subscription && subscription.plan && subscription.plan.shortFeatures && subscription.plan.shortFeatures.active === 'true'
      );

      for (const tab in tabs) {
        for (const category of categories) {
          const plan = await this._getTabs(category, tab);
          if (plan) {
            tabs[tab].push(plan);
          }
        }
      }

      this.subscriptionsTabs = tabs;

    } catch (error) {
      console.error(error);
    }
  }

  private async _getTabs(category, tab): Promise<any> {
    const subscription = this.userHostingSubscriptions.find((userSubscription: any) => {
      return (this.isTier1Country || this.englishCountries) ?
        this._getSubscription(userSubscription, category, tab, 'b') :
        this._getSubscription(userSubscription, category, tab, 'a');
    });
    return subscription;
  }

  private _getSubscription(subscription, category, tab, variant): boolean {
    return (
      subscription &&
      subscription.plan &&
      subscription.variant === variant &&
      subscription.category === category &&
      subscription.tabCategory === tab &&
      subscription.plan.periodNumber === this.periodSwitch.value &&
      !!this.planFeatures?.[tab]?.[category]
    );
  }


  private _sendEvent(chosenPlan): void {
    const analyticsData = {
      eventCategory: 'Dashboard Pageview',
      eventAction: "Checkout Page visit" + (!!this.paidFeature ? '( Purchase pop-up)' : ' 1wf pop-up'),
      eventLabel: chosenPlan.title + ' ' + chosenPlan.type + 'ly'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  private addEventHandlers(): void {
    this._subscriptions = [];
    this._subscriptions.push(this.periodSwitch.valueChanges.subscribe((): void => {
      this.getAvailableSubscriptions();
    }));
  }

  private _applyCoupon(subscriptionId, coupon): void {
    this._authService.applyCoupon(subscriptionId, coupon)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response) => {
        if (response.success) {
          this.applyCouponForPlatform = true;
          this.couponPercent = response.data.first_amount;
          this.hasFreeDays = response.data.free_days_before_payment && response.data.free_days_before_payment !== 0;
        }
      });
  }

}
